// src/api/queries/comments.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export const COMMENTS_KEY = "comments";

export function useCommentsQuery({ projectId, taskId }, options = {}) {
  return useQuery({
    queryKey: [COMMENTS_KEY, projectId ?? null, taskId ?? null],
    queryFn: () => {
      const params = new URLSearchParams();
      if (projectId) params.set("projectId", projectId);
      if (taskId) params.set("taskId", taskId);
      return api.get(`/comments?${params.toString()}`);
    },
    enabled: !!projectId || !!taskId,
    ...options,
  });
}

export function useAddCommentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/comments", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["unread-counts"] });
    },
  });
}

export function useUpdateCommentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }) => api.patch(`/comments/${id}`, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
    },
  });
}

export function useDeleteCommentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/comments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
      qc.invalidateQueries({ queryKey: ["unread-counts"] });
    },
  });
}

export function useReactMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, emoji }) =>
      api.post(`/comments/${id}/reactions`, { emoji }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
    },
  });
}

export function usePinCommentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/comments/${id}/pin`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
    },
  });
}
